import { Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { analytics } from '../../analytics';
import { useTourOptional } from '../tour-context';

const HIDDEN_KEY = 'nucleus.tour.welcome.hidden';
const DELAY_MS = 1500;
const MIN_HEIGHT = 500;

function hiddenThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

function hideThisSession(): void {
  try {
    window.sessionStorage.setItem(HIDDEN_KEY, '1');
  } catch {
    // Without storage the prompt can come back on the next page load; harmless.
  }
}

export interface WelcomePromptProps {
  /** The app decides: runtime config, mock mode, support session, two-factor gate. */
  allowed: boolean;
  firstName: string;
}

/**
 * The one thing that ever appears by itself: a small non-modal card offering the welcome tour,
 * once per person (until they answer) and at most once per browser session.
 */
export function WelcomePrompt({ allowed, firstName }: WelcomePromptProps) {
  const tour = useTourOptional();
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);

  const welcome = tour?.registry.get(tour.welcomeTourId);
  const eligible =
    allowed &&
    tour !== null &&
    tour.enabled &&
    !tour.progress.readOnly &&
    !tour.progress.isPending &&
    !tour.progress.isError &&
    welcome !== undefined &&
    !tour.progress.records.has(welcome.id) &&
    tour.state.phase === 'idle';

  useEffect(() => {
    if (!eligible || hiddenThisSession()) return;
    const id = window.setTimeout(() => {
      if (window.innerHeight < MIN_HEIGHT) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"]'))
        return;
      hideThisSession();
      setVisible(true);
    }, DELAY_MS);
    return () => window.clearTimeout(id);
  }, [eligible]);

  if (!visible || !tour || !welcome) return null;

  const answer = (choice: 'start' | 'not-now' | 'never') => {
    analytics.track('welcome_prompt_answered', { answer: choice });
    setVisible(false);
    if (choice === 'start') void tour.start(welcome.id, { source: 'welcome' });
    if (choice === 'never') tour.dismissWithoutRunning(welcome.id);
  };

  return (
    <section
      aria-labelledby="tour-welcome-title"
      className={cn(
        'bg-card text-card-foreground fixed inset-x-4 bottom-4 z-[60] flex flex-col gap-3 rounded-xl border p-4 shadow-lg md:inset-x-auto md:right-6 md:bottom-6 md:w-80',
        !tour.reducedMotion &&
          'animate-in fade-in-0 slide-in-from-bottom-4 motion-reduce:slide-in-from-bottom-0 duration-200',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h2 id="tour-welcome-title" className="flex items-center gap-2 text-base font-semibold">
          <Sparkles aria-hidden className="text-primary size-4" />
          {t('tour.welcome.title', { firstName })}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          className="-mt-1 -mr-2 size-7"
          aria-label={t('tour.welcome.notNow')}
          onClick={() => answer('not-now')}
        >
          <X aria-hidden />
        </Button>
      </div>
      <p className="text-muted-foreground text-sm">{t('tour.welcome.body')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => answer('start')}>
          {t('tour.welcome.start')}
        </Button>
        <Button size="sm" variant="outline" onClick={() => answer('not-now')}>
          {t('tour.welcome.notNow')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => answer('never')}>
          {t('tour.welcome.never')}
        </Button>
      </div>
    </section>
  );
}
